import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { 
  FaArrowLeft, FaSave, FaPlus, FaTrash, FaSpinner,
  FaBuilding, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaTruck, FaWeightHanging, FaDollarSign, FaCalculator,
  FaShieldAlt, FaFileAlt, FaInfoCircle, FaExclamationTriangle,
  FaCheckCircle, FaTimes, FaCalendarAlt, FaUserTie,
  FaMoneyBillWave, FaClock, FaBoxes, FaWarehouse,
  FaRegFilePdf, FaEnvelope as FaEnvelopeIcon, FaDownload, FaEye
} from 'react-icons/fa';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import AdminLayout from '../layouts/AdminLayout';
import { quotes, clients, users } from '../api';
import { API_URL } from '../config';
import InteractiveRouteMap from '../components/InteractiveRouteMap';
import QuotePDF from '../components/QuotePDF';

// ============================================
// GOOGLE MAPS - shared loader (hooks/useGoogleMaps)
// ============================================

// Company details for the PDF come from the single source
// (config/CompanyDetails) — QuotePDF defaults to it when no
// companyDetails prop is passed.

// ============================================
// QUOTE VALIDITY - the window the form can actually show
// ============================================
// The Expiry <select> offers 7 / 14 / 30 / 60 days and nothing else. Loading a quote for editing
// used to derive the window straight from the stored dates, which can be ANY number (the LHM quote
// came back as 61). A <select> whose value matches no option silently displays its FIRST option - so
// the screen said "7 Days" while the quote's real window was 61 days, and saving kept the long date.
// The user sees one thing, the database holds another. That is the entire bug.
//
// Snapping to the nearest offered option makes the dropdown tell the truth.
// KEEP THIS LIST IN STEP WITH THE EXPIRY <select> BELOW.
// ============================================
const EXPIRY_DAYS_OPTIONS = [7, 14, 30, 60];

const nearestExpiryDays = (q) => {
  // An explicit, supported window wins.
  const stored = Number(q && q.expiry_days);
  if (EXPIRY_DAYS_OPTIONS.includes(stored)) return stored;

  // Otherwise work it out from what the quote actually holds.
  let windowDays = null;
  if (q && q.expiry_date && q.created_at) {
    windowDays = Math.round((new Date(q.expiry_date) - new Date(q.created_at)) / 86400000);
  }
  if (!windowDays || windowDays <= 0) return 30;

  return EXPIRY_DAYS_OPTIONS.reduce(
    (best, option) => (Math.abs(option - windowDays) < Math.abs(best - windowDays) ? option : best),
    EXPIRY_DAYS_OPTIONS[0]
  );
};

function CreateQuote({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // ✅ Google Maps API Loader (shared hook — fixes the first-visit "Loading Google Maps..." hang)
  const { isLoaded } = useGoogleMaps();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState({});
  const [clientsList, setClientsList] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    quote_type: 'minerals',
    customer_id: '',
    customer_name: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    salesperson_id: '',
    payment_terms: '30 Days',
    terms_conditions: [],
    custom_terms: '',
    vat_included: false,
    expiry_days: 30,
    
    // Minerals/Agri
    commodity: '',
    tonnage: '',
    origin: '',
    destination: '',
    distance_km: '',
    rate_per_ton: '',
    wet_or_dry: 'dry',
    truck_type: 'side_tipper',
    truck_type_other: '',
    
    // Abnormal
    description: '',
    subbie_quote: '',
    margin_percentage: '',
    margin_amount: '',
    client_price: '',
    
    // General
    services: '',
    hourly_rate: '',
    estimated_hours: '',
    
    // Items - with cost_price and margin_percentage for abnormals
    items: [{ description: '', quantity: 1, cost_price: 0, margin_percentage: '', unit_price: 0, total: 0 }],
    
    // Route
    route: {
      pickup_address: '',
      delivery_address: '',
      distance_km: ''
    },
    
    comments: '',
    quote_number: '',
    created_at: '',
    expiry_date: '',
    total_amount: 0,
    vat_amount: 0,
    grand_total: 0,
    quote_type_label: '',
    
    // Customer Type
    is_other_customer: false,
    customer_vat: '',
    customer_address: ''
  });

  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);

  // ✅ Client contacts for contact-picker in customer section
  const [clientContacts, setClientContacts] = useState([]);

  const fetchClientContacts = async (clientId) => {
    if (!clientId) { setClientContacts([]); return; }
    try {
      const response = await fetch(`${API_URL}/clients/${clientId}/contacts`);
      const data = await response.json();
      setClientContacts(data.success ? data.contacts || [] : []);
    } catch (err) {
      console.error('Error fetching client contacts:', err);
      setClientContacts([]);
    }
  };

  // ONE list for the "Contact Person" picker, so it ALWAYS works:
  //   1. the client's stored main contact (clients.contact_person/email/phone), then
  //   2. every saved contact for that client (client_contacts).
  // De-duped by email (falling back to name), and names are TRIMMED.
  // Why: the picker used to be hidden whenever a client had no saved contacts (which is
  // most of them), it matched on the raw name, and stored names often carry stray
  // spaces (" Lyndall") - so the selection silently did not stick.
  const contactOptions = () => {
    const list = [];
    const seen = new Set();
    const push = (name, email, phone, isMain) => {
      const nm = String(name || '').trim();
      if (!nm) return;
      const em = String(email || '').trim();
      const key = em ? em.toLowerCase() : nm.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push({ name: nm, email: em, phone: String(phone || '').trim(), isMain: !!isMain });
    };
    if (!formData.is_other_customer) {
      const c = (clientsList || []).find(x => x.id === formData.customer_id) || {};
      push(c.contact_person, c.contact_email, c.contact_phone, true);
    }
    (clientContacts || []).forEach(c => push(c.contact_person, c.email, c.phone, c.is_primary));
    // Anything already stored/typed that is not in the lists above stays selectable.
    const current = String(formData.contact_person || '').trim();
    if (current && !list.some(c => c.name === current)) {
      list.push({
        name: current,
        email: String(formData.contact_email || '').trim(),
        phone: String(formData.contact_phone || '').trim(),
        isMain: false
      });
    }
    return list;
  };

  const userData = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    setUser(userData);
    fetchData();
    if (id) {
      setIsEditing(true);
      fetchQuote(id);
    }
    // ── Pre-fill from CRM: if ?company=X&contact=Y&email=Z in URL, select "Other Customer" ──
    const params = new URLSearchParams(window.location.search);
    const crmCompany = params.get('company');
    if (crmCompany) {
      setFormData(prev => ({
        ...prev,
        is_other_customer: true,
        customer_id: '',  // clear any selected client
        customer_name: crmCompany,
        contact_person: params.get('contact') || '',
        contact_email: params.get('email') || ''
      }));
    }
  }, [id]);

  const fetchData = async () => {
    try {
      const clientsData = await clients.getAll();
      if (clientsData.success) {
        setClientsList(clientsData.clients || []);
      }
      
      const usersData = await users.getAll();
      if (usersData.success) {
        setSalespersons(usersData.users?.filter(u => u.role === 'admin' || u.role === 'super_admin') || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const fetchQuote = async (quoteId) => {
    setLoading(true);
    try {
      const response = await quotes.getOne(quoteId);
      if (response.success) {
        const q = response.quote;
        setFormData({
          quote_type: q.quote_type || 'minerals',
          customer_id: q.customer_id || '',
          customer_name: q.customer_name || '',
          contact_person: q.contact_person || '',
          contact_phone: q.contact_phone || '',
          contact_email: q.contact_email || '',
          salesperson_id: q.salesperson_id || '',
          payment_terms: q.payment_terms || '30 Days',
          terms_conditions: q.terms_conditions ? q.terms_conditions.split(',') : [],
          custom_terms: q.custom_terms || '',
          vat_included: q.vat_included || false,
          // Always one of the options the Expiry dropdown offers, so the screen cannot lie about the
          // window this quote actually has. (See nearestExpiryDays above.)
          expiry_days: nearestExpiryDays(q),
          
          commodity: q.commodity || '',
          tonnage: q.tonnage || '',
          origin: q.origin || '',
          destination: q.destination || '',
          distance_km: q.distance_km || '',
          rate_per_ton: q.rate_per_ton || '',
          wet_or_dry: q.wet_or_dry || 'dry',
          truck_type: q.truck_type || 'side_tipper',
          truck_type_other: q.truck_type_other || '',
          
          description: q.description || '',
          subbie_quote: q.subbie_quote || '',
          margin_percentage: q.margin_percentage || '',
          margin_amount: q.margin_amount || '',
          client_price: q.client_price || '',
          
          services: q.services || '',
          hourly_rate: q.hourly_rate || '',
          estimated_hours: q.estimated_hours || '',
          
          items: q.items && q.items.length > 0 ? q.items : [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
          
          route: {
            pickup_address: q.route?.pickup_address || '',
            delivery_address: q.route?.delivery_address || '',
            distance_km: q.route?.distance_km || ''
          },
          
          comments: q.comments || '',
          quote_number: q.quote_number || '',
          created_at: q.created_at || '',
          expiry_date: q.expiry_date || '',
          total_amount: q.total_amount || 0,
          vat_amount: q.vat_amount || 0,
          grand_total: q.grand_total || 0,
          is_other_customer: false
        });
        
        // ✅ Restore coordinates from stored route lat/lng (much faster than geocoding)
        if (q.route?.pickup_lat && q.route?.pickup_lng) {
          setOriginCoords({ lat: q.route.pickup_lat, lng: q.route.pickup_lng, address: q.origin || q.route.pickup_address || '' });
        } else if (q.origin) {
          // Fallback: geocode if no stored lat/lng
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: q.origin }, (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                const loc = results[0].geometry.location;
                const coords = { lat: loc.lat(), lng: loc.lng(), address: q.origin };
                setOriginCoords(coords);
              }
            });
          } catch (e) { /* geocode error, non-critical */ }
        }
        if (q.route?.delivery_lat && q.route?.delivery_lng) {
          setDestCoords({ lat: q.route.delivery_lat, lng: q.route.delivery_lng, address: q.destination || q.route.delivery_address || '' });
        } else if (q.destination) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: q.destination }, (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                const loc = results[0].geometry.location;
                const coords = { lat: loc.lat(), lng: loc.lng(), address: q.destination };
                setDestCoords(coords);
              }
            });
          } catch (e) { /* geocode error, non-critical */ }
        }

        if (!q.customer_id && q.customer_name) {
          setFormData(prev => ({ ...prev, is_other_customer: true }));
        }

        // Load the client contact list on edit so the dropdown appears and pre-selects the saved person.
        if (q.customer_id) {
          fetchClientContacts(q.customer_id);
        }
      } else {
        toast.error('Failed to load quote');
        navigate('/quotes');
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      toast.error('Failed to load quote');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HANDLE CHANGES
  // ============================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // A quantity-2 line of a DISCRETE movement (crane move, abnormal load, rigging,
  // standing time, once-off trip) should become TWO job lines so each can be
  // allocated to a different subbie / date / completion. BULK haulage (minerals
  // in MT, container volume) must stay as ONE line with quantity N.
  // `split_lines === undefined` = auto, decided from the description.
  const looksDiscrete = (desc = '') =>
    /crane|abnormal|rigging|standing|trip|escort|survey|permit|mobilis|demobilis|once[- ]?off|installation|erection/i.test(desc);
  const splitEffective = (item) =>
    item.split_lines !== undefined && item.split_lines !== null
      ? item.split_lines
      : looksDiscrete(item.description || '');

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const cost = parseFloat(updatedItems[index].cost_price) || 0;
    const marginPct = parseFloat(updatedItems[index].margin_percentage) || 0;
    let price = parseFloat(updatedItems[index].unit_price) || 0;
    
    // Smart auto-calculation per item
    if (field === 'cost_price' && marginPct > 0 && cost > 0) {
      // Cost changed with a margin % set → recalc unit price
      price = cost * (1 + marginPct / 100);
      updatedItems[index].unit_price = price.toFixed(2);
    } else if (field === 'margin_percentage' && cost > 0 && marginPct > 0) {
      // Margin % changed with a cost → recalc unit price
      price = cost * (1 + marginPct / 100);
      updatedItems[index].unit_price = price.toFixed(2);
    } else if (field === 'unit_price' && cost > 0 && price > 0) {
      // Unit price changed with a cost → recalc margin %
      const calcPct = ((price - cost) / cost) * 100;
      updatedItems[index].margin_percentage = calcPct.toFixed(1);
    }
    
    // Always recalculate total
    const finalPrice = parseFloat(updatedItems[index].unit_price) || 0;
    updatedItems[index].total = qty * finalPrice;
    
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = (hasCostPricing = false) => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, cost_price: 0, margin_percentage: '', unit_price: 0, total: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      toast.warning('You need at least one item');
      return;
    }
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  // ============================================
  // CALCULATIONS
  // ============================================
  const calculateTotals = () => {
    let subtotal = 0;
    
    for (const item of formData.items) {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      subtotal += qty * price;
    }
    
    if ((formData.quote_type === 'minerals' || formData.quote_type === 'agri') && formData.tonnage && formData.rate_per_ton) {
      const tonnage = parseFloat(formData.tonnage) || 0;
      const rate = parseFloat(formData.rate_per_ton) || 0;
      if (tonnage > 0 && rate > 0 && subtotal === 0) {
        subtotal = tonnage * rate;
      }
    }
    
    if (formData.quote_type === 'general' && formData.estimated_hours && formData.hourly_rate) {
      const hours = parseFloat(formData.estimated_hours) || 0;
      const rate = parseFloat(formData.hourly_rate) || 0;
      if (hours > 0 && rate > 0 && subtotal === 0) {
        subtotal = hours * rate;
      }
    }
    
    const vatAmount = formData.vat_included ? subtotal * 0.15 : 0;
    const grandTotal = subtotal + vatAmount;
    
    return { subtotal, vatAmount, grandTotal };
  };

  const calculateMargin = () => {
    const subbie = parseFloat(formData.subbie_quote) || 0;
    const client = parseFloat(formData.client_price) || 0;
    const marginPercent = parseFloat(formData.margin_percentage) || 0;
    const marginAmount = parseFloat(formData.margin_amount) || 0;
    
    if (subbie > 0 && client > 0) {
      const profit = client - subbie;
      const percentage = (profit / client) * 100;
      return { profit, percentage };
    }
    
    if (subbie > 0 && marginPercent > 0) {
      const amount = subbie * (marginPercent / 100);
      const price = subbie + amount;
      return { profit: amount, percentage: marginPercent, clientPrice: price };
    }
    
    if (subbie > 0 && marginAmount > 0) {
      const percentage = (marginAmount / subbie) * 100;
      const price = subbie + marginAmount;
      return { profit: marginAmount, percentage, clientPrice: price };
    }
    
    return null;
  };

  // ============================================
  // SUBMIT
  // ============================================
  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();
    setSaving(true);

    try {
      if (!formData.quote_type) {
        toast.error('Please select a quote type');
        setSaving(false);
        return;
      }

      const payload = {
        quote_type: formData.quote_type,
        customer_id: formData.is_other_customer ? null : formData.customer_id || null,
        customer_name: formData.is_other_customer ? formData.customer_name : formData.customer_name || null,
        contact_person: formData.is_other_customer ? formData.contact_person : formData.contact_person || null,
        contact_phone: formData.is_other_customer ? formData.contact_phone : formData.contact_phone || null,
        contact_email: formData.is_other_customer ? formData.contact_email : formData.contact_email || null,
        customer_vat: formData.customer_vat || null,
        customer_address: formData.customer_address || null,
        salesperson_id: formData.salesperson_id || null,
        payment_terms: formData.payment_terms,
        terms_conditions: formData.terms_conditions.length > 0 ? formData.terms_conditions.join(',') : 'standard',
        custom_terms: formData.custom_terms || null,
        vat_included: formData.vat_included,
        expiry_days: formData.expiry_days,
        comments: formData.comments || null,
        status: status,
        items: formData.items
          .filter(item => item.description.trim())
          .map(item => ({
            ...item,
            unit: item.unit || 'load',
            // Only meaningful for qty > 1; the backend expands these into one job
            // line per unit (discrete movements) and leaves qty as-is otherwise.
            split_lines: (parseFloat(item.quantity) || 1) > 1 ? !!splitEffective(item) : false
          })),
        route: {
          ...formData.route,
          pickup_lat: originCoords?.lat || null,
          pickup_lng: originCoords?.lng || null,
          delivery_lat: destCoords?.lat || null,
          delivery_lng: destCoords?.lng || null
        },
        created_by: user.id
      };

      if (formData.quote_type === 'minerals' || formData.quote_type === 'agri') {
        payload.commodity = formData.commodity;
        payload.tonnage = parseFloat(formData.tonnage) || 0;
        payload.origin = formData.origin;
        payload.destination = formData.destination;
        payload.distance_km = parseFloat(formData.distance_km) || 0;
        payload.rate_per_ton = parseFloat(formData.rate_per_ton) || 0;
        payload.wet_or_dry = formData.wet_or_dry;
        payload.truck_type = formData.truck_type;
        payload.truck_type_other = formData.truck_type === 'other' ? formData.truck_type_other : null;
      }

      if (formData.quote_type === 'abnormal') {
        payload.description = formData.description;
        payload.subbie_quote = parseFloat(formData.subbie_quote) || 0;
        payload.margin_percentage = parseFloat(formData.margin_percentage) || 0;
        payload.margin_amount = parseFloat(formData.margin_amount) || 0;
        payload.client_price = parseFloat(formData.client_price) || 0;
      }

      if (formData.quote_type === 'general') {
        // General now uses same payload as Abnormal (line items + margin)
        payload.description = formData.description || formData.services;
        payload.subbie_quote = parseFloat(formData.subbie_quote) || 0;
        payload.margin_percentage = parseFloat(formData.margin_percentage) || 0;
        payload.margin_amount = parseFloat(formData.margin_amount) || 0;
        payload.client_price = parseFloat(formData.client_price) || 0;
      }

      let response;
      if (isEditing) {
        response = await quotes.update(id, payload);
      } else {
        response = await quotes.create(payload);
      }

      if (response.success) {
        toast.success(`✅ Quote ${status === 'draft' ? 'saved' : 'created'} successfully!`);
        navigate('/quotes');
      } else {
        toast.error(response.error || 'Failed to save quote');
      }
    } catch (err) {
      console.error('Error saving quote:', err);
      toast.error('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // HANDLE SEND
  // ============================================
  const handleSend = async () => {
    if (!id) {
      toast.error('Please save the quote first before sending');
      return;
    }
    
    try {
      const response = await quotes.send(id);
      if (response.success) {
        toast.success(`✅ Quote sent successfully!`);
        navigate('/quotes');
      } else {
        toast.error(response.error || 'Failed to send quote');
      }
    } catch (err) {
      console.error('Error sending quote:', err);
      toast.error('Failed to send quote');
    }
  };

  // ============================================
  // LOCATION PICKER
  // ============================================
  const handleOriginChange = (address, coords) => {
    setOriginCoords(coords);
    setFormData(prev => ({
      ...prev,
      origin: address,
      route: { ...prev.route, pickup_address: address }
    }));
  };

  const handleDestinationChange = (address, coords) => {
    setDestCoords(coords);
    setFormData(prev => ({
      ...prev,
      destination: address,
      route: { ...prev.route, delivery_address: address }
    }));
  };

  // ============================================
  // >>> ROUTE CAPTURE <<<
  // ============================================
  // InteractiveRouteMap now hands us the route it ACTUALLY drew — including a route the user
  // dragged/rerouted. We keep the whole payload in formData.route.route_optimized, which the
  // POST spreads straight out (see the payload built in handleSubmit), and the backend stores
  // it on `quote_routes.route_optimized` so the load pages can redraw the EXACT quoted route.
  const handleRouteCaptured = (routePayload) => {
    setFormData(prev => ({
      ...prev,
      route: { ...prev.route, route_optimized: routePayload }
    }));
  };

  const handleDistanceCalculated = (data) => {
    setFormData(prev => ({
      ...prev,
      distance_km: data.distance_km,
      route: { ...prev.route, distance_km: data.distance_km }
    }));
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const renderItemsTable = () => {
    const hasCostPricing = formData.quote_type === 'abnormal' || formData.quote_type === 'general';
    // Calculate cost totals when showing cost pricing
    const totalCost = hasCostPricing 
      ? formData.items.reduce((sum, item) => sum + (parseFloat(item.cost_price) || 0) * (parseFloat(item.quantity) || 0), 0)
      : 0;
    const totalClient = formData.items.reduce((sum, item) => sum + (parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0), 0);
    const totalMargin = totalClient - totalCost;
    const marginPct = totalCost > 0 ? (totalMargin / totalCost) * 100 : 0;

    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
          <span className="text-sm font-medium text-navy">Line Items</span>
          <button
            type="button"
            onClick={addItem}
            className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-blue-700 transition-colors"
          >
            <FaPlus size={12} /> Add Item
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-24">Qty</th>
                {hasCostPricing && (
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28 bg-blue-50 text-blue-700">
                    Cost Price (Subbie)
                  </th>
                )}
                {hasCostPricing && (
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20 bg-blue-50 text-blue-700">
                    Margin %
                  </th>
                )}
                <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">
                  {hasCostPricing ? 'Client Price' : 'Unit Price'}
                </th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Total</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-12">Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, index) => {
                const itemQty = parseFloat(item.quantity) || 1;
                const itemCost = parseFloat(item.cost_price) || 0;
                const itemPrice = parseFloat(item.unit_price) || 0;
                const itemTotal = itemQty * itemPrice;
                const itemMarginPct = itemCost > 0 && itemPrice > 0 ? ((itemPrice - itemCost) / itemCost) * 100 : 0;
                return (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
                        placeholder="Item description..."
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={parseInt(item.quantity) || 1}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-green"
                      />
                      {itemQty > 1 && (
                        <label
                          className="mt-1 flex items-center justify-center gap-1 text-[10px] text-gray-500 cursor-pointer"
                          title="Create one separate job line per unit, so each load can be allocated to a different subbie or date. Untick for bulk haulage."
                        >
                          <input
                            type="checkbox"
                            checked={splitEffective(item)}
                            onChange={(e) => handleItemChange(index, 'split_lines', e.target.checked)}
                            className="w-3 h-3 accent-green"
                          />
                          Split → {Math.round(itemQty)} lines
                        </label>
                      )}
                    </td>
                    {hasCostPricing && (
                      <td className="px-3 py-2 bg-blue-50/50">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.cost_price}
                          onChange={(e) => handleItemChange(index, 'cost_price', e.target.value)}
                          className="w-full px-2 py-1 border border-blue-200 rounded-lg text-sm text-right focus:outline-none focus:border-blue-500 bg-white"
                          placeholder="0.00"
                        />
                      </td>
                    )}
                    {hasCostPricing && (
                      <td className="px-3 py-2 bg-blue-50/50">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={item.margin_percentage}
                          onChange={(e) => handleItemChange(index, 'margin_percentage', e.target.value)}
                          className="w-full px-2 py-1 border border-blue-200 rounded-lg text-sm text-center focus:outline-none focus:border-blue-500 bg-white"
                          placeholder="%"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-green"
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-navy">
                      R{itemTotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-400 hover:text-red-600 transition-colors"
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Cost Summary Footer for Abnormal */}
        {hasCostPricing && totalCost > 0 && (
          <div className="bg-blue-50 px-4 py-2 border-t border-blue-200 flex justify-end gap-6 text-sm">
            <span>Total Cost: <strong className="text-blue-700">R{totalCost.toFixed(2)}</strong></span>
            <span>Total Client: <strong className="text-green-700">R{totalClient.toFixed(2)}</strong></span>
            <span>Total Margin: <strong className={totalMargin >= 0 ? 'text-green-700' : 'text-red-700'}>R{totalMargin.toFixed(2)} ({marginPct.toFixed(1)}%)</strong></span>
          </div>
        )}
      </div>
    );
  };

  const renderMarginCalculator = () => {
    const margin = calculateMargin();
    return (
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <FaCalculator className="text-blue-600" /> Margin Calculator
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subbie Quote (R)</label>
            <input
              type="number"
              name="subbie_quote"
              value={formData.subbie_quote}
              onChange={(e) => {
                const val = e.target.value;
                setFormData(prev => {
                  const subbie = parseFloat(val) || 0;
                  const pct = parseFloat(prev.margin_percentage) || 0;
                  const amt = parseFloat(prev.margin_amount) || 0;
                  const update = { ...prev, subbie_quote: val };
                  // Auto-fill the other field based on which has a value
                  if (pct > 0 && subbie > 0) {
                    update.margin_amount = (subbie * (pct / 100)).toFixed(2);
                  } else if (amt > 0 && subbie > 0) {
                    update.margin_percentage = ((amt / subbie) * 100).toFixed(1);
                  }
                  return update;
                });
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Margin %</label>
            <input
              type="number"
              name="margin_percentage"
              value={formData.margin_percentage}
              onChange={(e) => {
                const pct = parseFloat(e.target.value) || 0;
                setFormData(prev => {
                  const subbie = parseFloat(prev.subbie_quote) || 0;
                  const update = { ...prev, margin_percentage: e.target.value };
                  if (pct > 0 && subbie > 0) {
                    update.margin_amount = (subbie * (pct / 100)).toFixed(2);
                  } else {
                    update.margin_amount = '';
                  }
                  return update;
                });
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
              placeholder="15"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Margin Amount (R)</label>
            <input
              type="number"
              name="margin_amount"
              value={formData.margin_amount}
              onChange={(e) => {
                const amt = parseFloat(e.target.value) || 0;
                setFormData(prev => {
                  const subbie = parseFloat(prev.subbie_quote) || 0;
                  const update = { ...prev, margin_amount: e.target.value };
                  if (amt > 0 && subbie > 0) {
                    update.margin_percentage = ((amt / subbie) * 100).toFixed(1);
                  } else {
                    update.margin_percentage = '';
                  }
                  return update;
                });
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-green-800">Client Price:</span>
            <span className="text-xl font-bold text-green-700">
              R{(margin?.clientPrice || 0).toFixed(2)}
            </span>
          </div>
          {margin && (
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600">Profit: R{margin.profit.toFixed(2)}</span>
              <span className="text-gray-600">Margin: {margin.percentage.toFixed(1)}%</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCPK = () => {
    const tonnage = parseFloat(formData.tonnage) || 0;
    const rate = parseFloat(formData.rate_per_ton) || 0;
    const distance = parseFloat(formData.distance_km) || 0;
    
    if (tonnage > 0 && rate > 0 && distance > 0) {
      const total = tonnage * rate;
      const cpk = total / distance;
      return (
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-purple-800">CPK (Cost Per Kilometer):</span>
            <span className="text-lg font-bold text-purple-700">R{cpk.toFixed(2)}/km</span>
          </div>
          <div className="text-xs text-purple-600 mt-1">
            {tonnage} MT × R{rate} = R{total.toFixed(2)} / {distance} km
          </div>
        </div>
      );
    }
    return null;
  };

  const renderTotals = () => {
    const totals = calculateTotals();
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium text-navy">R{totals.subtotal.toFixed(2)}</span>
          </div>
          {formData.vat_included && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">VAT (15%)</span>
              <span className="font-medium text-navy">R{totals.vatAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-gray-200 pt-2 flex justify-between">
            <span className="text-lg font-bold text-navy">Grand Total</span>
            <span className="text-lg font-bold text-green-600">R{totals.grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // DYNAMIC LAYOUTS
  // ============================================
  const renderMineralsAgriLayout = () => {
    return (
      <>
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Commodity Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commodity *</label>
              <input
                type="text"
                name="commodity"
                value={formData.commodity}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. Chrome, Maize"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tonnage (MT) *</label>
              <input
                type="number"
                name="tonnage"
                value={formData.tonnage}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 1000"
                required
              />
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Route Details</h3>
          <div className="space-y-4">
            <InteractiveRouteMap
              originValue={formData.origin}
              destinationValue={formData.destination}
              initialOriginCoords={originCoords}
              initialDestCoords={destCoords}
              onOriginChange={(val) => setFormData(prev => ({ ...prev, origin: val }))}
              onDestinationChange={(val) => setFormData(prev => ({ ...prev, destination: val }))}
              onOriginCoordsChange={(data) => {
                if (data) {
                  setOriginCoords(data);
                  setFormData(prev => ({
                    ...prev,
                    route: { ...prev.route, pickup_address: data.address || data.formatted_address || '' }
                  }));
                }
              }}
              onDestCoordsChange={(data) => {
                if (data) {
                  setDestCoords(data);
                  setFormData(prev => ({
                    ...prev,
                    route: { ...prev.route, delivery_address: data.address || data.formatted_address || '' }
                  }));
                }
              }}
              onDistanceCalculated={handleDistanceCalculated}
              onRouteChange={handleRouteCaptured}
            />
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Pricing & Specifications</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Ton (R) *</label>
              <input
                type="number"
                name="rate_per_ton"
                value={formData.rate_per_ton}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 450"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wet / Dry</label>
              <select
                name="wet_or_dry"
                value={formData.wet_or_dry}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="dry">Dry</option>
                <option value="wet">Wet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type *</label>
              <select
                name="truck_type"
                value={formData.truck_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="side_tipper">Side Tipper</option>
                <option value="flat_deck">Flat Deck</option>
                <option value="lowbed">Lowbed</option>
                <option value="tautliner">Tautliner</option>
                <option value="grain_side_tipper">Grain Side Tipper</option>
                <option value="extended_trailer">Extended Trailer</option>
                <option value="step_deck">Step Deck</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              {formData.truck_type === 'other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type Description</label>
                  <input
                    type="text"
                    name="truck_type_other"
                    value={formData.truck_type_other}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    placeholder="Describe truck type..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {renderCPK()}
        {renderItemsTable()}
        {renderMarginCalculator()}
        {renderTotals()}
      </>
    );
  };

  const renderAbnormalLayout = () => {
    return (
      <>
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Job Description</h3>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            rows="4"
            placeholder="Describe the abnormal load / job requirements..."
            required
          />
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Route Details</h3>
          <div className="space-y-4">
            <InteractiveRouteMap
              originValue={formData.origin}
              destinationValue={formData.destination}
              initialOriginCoords={originCoords}
              initialDestCoords={destCoords}
              onOriginChange={(val) => setFormData(prev => ({ ...prev, origin: val }))}
              onDestinationChange={(val) => setFormData(prev => ({ ...prev, destination: val }))}
              onOriginCoordsChange={(data) => {
                if (data) {
                  setOriginCoords(data);
                  setFormData(prev => ({
                    ...prev,
                    route: { ...prev.route, pickup_address: data.address || data.formatted_address || '' }
                  }));
                }
              }}
              onDestCoordsChange={(data) => {
                if (data) {
                  setDestCoords(data);
                  setFormData(prev => ({
                    ...prev,
                    route: { ...prev.route, delivery_address: data.address || data.formatted_address || '' }
                  }));
                }
              }}
              onDistanceCalculated={handleDistanceCalculated}
              onRouteChange={handleRouteCaptured}
            />
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Cost & Pricing</h3>
          {renderItemsTable()}
          {renderMarginCalculator()}
          {renderTotals()}
        </div>
      </>
    );
  };

  const renderGeneralLayout = () => {
    return (
      <>
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Service Details</h3>
          <textarea
            name="services"
            value={formData.services}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            rows="3"
            placeholder="Describe the services being quoted..."
            required
          />
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-navy mb-3">Pricing</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (R) *</label>
              <input
                type="number"
                name="hourly_rate"
                value={formData.hourly_rate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 800"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours *</label>
              <input
                type="number"
                name="estimated_hours"
                value={formData.estimated_hours}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 40"
                required
              />
            </div>
          </div>
        </div>

        {renderMarginCalculator()}
        {renderItemsTable()}
        {renderTotals()}
      </>
    );
  };

  // ============================================
  // PDF BUILDING
  // ============================================
  const buildQuoteForPDF = () => {
    const totals = calculateTotals();
    return {
      ...formData,
      total_amount: totals.subtotal,
      vat_amount: totals.vatAmount,
      grand_total: totals.grandTotal,
      salesperson: salespersons.find(sp => sp.id === formData.salesperson_id) || null
    };
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  if (!isLoaded) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={userData}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading Google Maps...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={userData}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading quote...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // If PDF preview is shown
  if (showPDFPreview) {
    const pdfQuote = buildQuoteForPDF();
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={userData}>
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setShowPDFPreview(false)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-navy flex items-center gap-2"
          >
            <FaArrowLeft size={20} /> Back to Edit
          </button>
          <div>
            <h1 className="text-2xl font-bold text-navy">📄 Quote Preview</h1>
            <p className="text-sm text-gray-500">{formData.quote_number || 'New Quote'}</p>
          </div>
          <div className="ml-auto flex gap-3">
            <PDFDownloadLink
              document={<QuotePDF quote={pdfQuote}  />}
              fileName={`${formData.quote_number || 'Quote'}.pdf`}
              className="flex items-center gap-2 px-5 py-2.5 bg-green text-white rounded-lg text-sm font-medium hover:bg-green transition-colors"
            >
              {({ loading }) => (
                <>
                  {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                  {loading ? 'Generating...' : 'Download PDF'}
                </>
              )}
            </PDFDownloadLink>
            {id && (
              <button
                onClick={handleSend}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <FaEnvelopeIcon /> Send Quote
              </button>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
          <PDFViewer width="100%" height="100%">
            <QuotePDF quote={pdfQuote}  />
          </PDFViewer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="quotes" onLogout={onLogout} user={userData}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/quotes" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-navy">
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-navy">
            {isEditing ? '✏️ Edit Quote' : '📄 Create Quote'}
          </h1>
          {isEditing && formData.quote_number && (
            <p className="text-sm text-gray-500">{formData.quote_number}</p>
          )}
        </div>
        <div className="ml-auto flex gap-3">
          {isEditing && id && (
            <button
              type="button"
              onClick={() => setShowPDFPreview(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-green hover:text-green transition-colors"
            >
              <FaEye size={14} /> Preview PDF
            </button>
          )}
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, 'draft')} className="space-y-6">
        {/* Customer Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <FaBuilding className="text-green-500" /> Customer Details
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'other') {
                        setFormData(prev => ({ ...prev, customer_id: '', is_other_customer: true }));
                        setClientContacts([]);
                      } else {
                        const customer = clientsList.find(c => c.id === val);
                        setFormData(prev => ({
                          ...prev,
                          customer_id: val,
                          is_other_customer: false,
                          customer_name: customer?.company_name || '',
                          contact_person: String(customer?.contact_person || '').trim(),
                          contact_phone: String(customer?.contact_phone || '').trim(),
                          contact_email: String(customer?.contact_email || '').trim()
                        }));
                        fetchClientContacts(val);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="">Select Customer...</option>
                    {clientsList.filter(c => c.status === 'active').map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                    <option value="other">➕ Other (New Customer)</option>
                  </select>
                </div>
                {!formData.is_other_customer && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <select
                      value={formData.contact_person}
                      onChange={(e) => {
                        const picked = contactOptions().find(c => c.name === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          contact_person: e.target.value,
                          contact_phone: picked?.phone || prev.contact_phone,
                          contact_email: picked?.email || prev.contact_email
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    >
                      <option value="">Select Contact...</option>
                      {contactOptions().map(c => (
                        <option key={`${c.name}|${c.email}`} value={c.name}>
                          {c.name}{c.isMain ? ' ⭐' : ''}{c.email ? '' : ' (no email on file)'}
                        </option>
                      ))}
                    </select>
                    {formData.contact_person && !formData.contact_email && (
                      <p className="text-xs text-amber-600 mt-1">
                        No email on file for this person — the Send button would open with no recipient.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {formData.is_other_customer && (
              <div className="col-span-2 bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 font-medium mb-3">New Customer Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. ABC Mining"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="+27 82 123 4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="info@company.co.za"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                    <input
                      type="text"
                      name="customer_vat"
                      value={formData.customer_vat}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. 4123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                    <input
                      type="text"
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. 123 Main Street, Johannesburg"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quote Type Selection */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <FaFileAlt className="text-green-500" /> Quote Type
          </h2>
          <div className="grid grid-cols-4 gap-4">
            {[
              { value: 'abnormal', label: '🚛 Abnormal', desc: 'Specialized loads' },
              { value: 'minerals', label: '⛏️ Minerals', desc: 'Mining commodities' },
              { value: 'agri', label: '🌾 Agri', desc: 'Agricultural goods' },
              { value: 'general', label: '📋 General', desc: 'Other services' }
            ].map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, quote_type: type.value }))}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  formData.quote_type === type.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-1">{type.label.split(' ')[0]}</div>
                <div className="font-medium text-navy">{type.label.split(' ')[1]}</div>
                <p className="text-xs text-gray-500 mt-1">{type.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Layout Based on Type */}
        {formData.quote_type === 'minerals' || formData.quote_type === 'agri' ? (
          renderMineralsAgriLayout()
        ) : formData.quote_type === 'abnormal' ? (
          renderAbnormalLayout()
        ) : formData.quote_type === 'general' ? (
          renderAbnormalLayout()
        ) : null}

        {/* Notes */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <FaInfoCircle className="text-green-500" /> Notes
          </h2>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            rows="3"
            placeholder="Any additional notes..."
          />
        </div>

        {/* Terms & Conditions */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <FaShieldAlt className="text-green-500" /> Terms & Conditions
          </h2>
          
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.terms_conditions.includes('standard')}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    terms_conditions: checked
                      ? [...prev.terms_conditions, 'standard']
                      : prev.terms_conditions.filter(t => t !== 'standard')
                  }));
                }}
                className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm">One Purpose Standard Terms & Conditions</span>
            </label>

            {formData.quote_type === 'abnormal' && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('abnormal')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'abnormal']
                        : prev.terms_conditions.filter(t => t !== 'abnormal')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm">One Purpose Abnormal Terms & Conditions</span>
              </label>
            )}

            {/* E&M Terms */}
            <div className="border-t border-gray-200 pt-3 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('enm')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'enm']
                        : prev.terms_conditions.filter(t => t !== 'enm')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">E&M Terms & Conditions</span>
              </label>
            </div>

            {/* Lovemore Terms */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('lovemore')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'lovemore']
                        : prev.terms_conditions.filter(t => t !== 'lovemore')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">Lovemore Terms & Conditions</span>
              </label>
            </div>

            {/* Transcor Terms */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('transcor')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'transcor']
                        : prev.terms_conditions.filter(t => t !== 'transcor')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">Transcor Terms & Conditions</span>
              </label>
            </div>

            <div className="border-t border-gray-200 pt-3 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('benecke')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'benecke']
                        : prev.terms_conditions.filter(t => t !== 'benecke')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">Benecke Transport Terms & Conditions</span>
              </label>
            </div>

            <div className="border-t border-gray-200 pt-3 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.terms_conditions.includes('custom')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      terms_conditions: checked
                        ? [...prev.terms_conditions, 'custom']
                        : prev.terms_conditions.filter(t => t !== 'custom')
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">Custom Terms</span>
              </label>
              
              {formData.terms_conditions.includes('custom') && (
                <textarea
                  name="custom_terms"
                  value={formData.custom_terms}
                  onChange={handleChange}
                  className="w-full mt-2 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green text-sm"
                  rows="4"
                  placeholder="Enter custom terms and conditions..."
                />
              )}
            </div>
          </div>
        </div>

        {/* Quote Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <FaCalendarAlt className="text-green-500" /> Quote Settings
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salesperson</label>
              <select
                name="salesperson_id"
                value={formData.salesperson_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="">Select...</option>
                {salespersons.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <select
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="Pay in Advance">Pay in Advance</option>
                <option value="COD">COD</option>
                <option value="7 Days">7 Days</option>
                <option value="14 Days">14 Days</option>
                <option value="30 Days">30 Days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
              <select
                name="expiry_days"
                value={formData.expiry_days}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="7">7 Days</option>
                <option value="14">14 Days</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  name="vat_included"
                  checked={formData.vat_included}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">Include VAT</span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions - Save only, Preview/Send from detail page */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Link
            to="/quotes"
            className="px-6 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

export default CreateQuote;